import React, { useRef, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { KeywordChip } from "../KeywordChip";
import chipStyles from "../KeywordChip/styles.module.scss";
import { Tooltip } from "../../../components/Tooltip";
import { SortArrowsIcon } from "../MyAppsTable/icons";
import { SetUpGearIcon, SetUpChevronIcon, CheckboxCheckIcon } from "./icons";
import {
  KeywordsTableColumn,
  KeywordsTableRowData,
  NewOrderKeywordsTableProps,
} from "./types";

const COLUMNS: { key: KeywordsTableColumn; label: string }[] = [
  { key: "keyword", label: "Keyword" },
  { key: "volume", label: "Volume" },
  { key: "rank", label: "Rank" },
  { key: "dateRange", label: "Date range" },
  { key: "installs", label: "Installs" },
  { key: "appOpening", label: "App opening" },
  { key: "total", label: "Total" },
];

const Checkbox: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}> = ({ checked, onChange, ariaLabel }) => (
  <label className={styles.checkbox}>
    <input
      type="checkbox"
      className={styles.checkboxInput}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={ariaLabel}
    />
    <span className={styles.checkboxBox} aria-hidden>
      {checked && <CheckboxCheckIcon className={styles.checkboxCheck} />}
    </span>
  </label>
);

/**
 * Keyword chip with an instant DS tooltip holding the full keyword — shown
 * only when the label is actually ellipsized by the 146px rail (measured on
 * hover, AppOverview TableRow behavior).
 */
const TruncatedKeywordChip: React.FC<{ label: string }> = ({ label }) => {
  const [truncated, setTruncated] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={wrapRef}
      className={styles.keywordChip}
      onMouseEnter={() => {
        const el = wrapRef.current?.querySelector<HTMLElement>(
          `.${chipStyles.label}`,
        );
        setTruncated(!!el && el.scrollWidth > el.clientWidth);
      }}
    >
      <Tooltip
        title={truncated ? label : undefined}
        compact
        placement="top"
        appendToBody
      >
        <KeywordChip label={label} />
      </Tooltip>
    </div>
  );
};

/** Numbered app-opening day boxes: filled 1..N, at least 2 boxes shown. */
const AppOpeningBoxes: React.FC<{ days: number }> = ({ days }) => {
  const total = Math.max(days, 2);
  return (
    <div className={styles.dayBoxes}>
      {Array.from({ length: total }, (_, index) => {
        const filled = index < days;
        return (
          <span
            key={index}
            className={cs(styles.dayBox, { [styles.dayBoxFilled]: filled })}
          >
            {filled ? index + 1 : ""}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Keywords setup table of the Search Terms order flow (Figma 1032:6303 header,
 * 1032:6305 row). Pure presentational: every value arrives pre-formatted in
 * `rows`; selection, sorting and the Set Up panel live in the host. The panel
 * itself renders through the `renderExpanded` slot under the open row.
 */
export const NewOrderKeywordsTable: React.FC<NewOrderKeywordsTableProps> = ({
  rows,
  onRowSelect,
  onSelectAll,
  onSetUpToggle,
  onSort,
  renderExpanded,
  className,
}) => {
  const allSelected = rows.length > 0 && rows.every((row) => row.selected);

  return (
    <div className={cs(styles.table, className)}>
      <div className={styles.headerRow}>
        {COLUMNS.map((column) => (
          <div key={column.key} className={styles.headerCell}>
            {column.key === "keyword" && (
              <Checkbox
                checked={allSelected}
                onChange={(checked) => onSelectAll?.(checked)}
                ariaLabel="Select all keywords"
              />
            )}
            {onSort ? (
              <button
                type="button"
                className={styles.headerLabelWrap}
                onClick={() => onSort(column.key)}
              >
                <span className={styles.headerLabel}>{column.label}</span>
                <SortArrowsIcon className={styles.sortIcon} />
              </button>
            ) : (
              <span className={styles.headerLabelWrap}>
                <span className={styles.headerLabel}>{column.label}</span>
                <SortArrowsIcon className={styles.sortIcon} />
              </span>
            )}
          </div>
        ))}
        {/* Set Up rail — no header label */}
        <div className={styles.headerCell} />
      </div>

      {rows.map((row) => (
        <React.Fragment key={row.keyword}>
          <div
            className={cs(styles.row, {
              [styles.rowActive]: row.selected || row.setUpOpen,
              [styles.rowOpen]: !!(row.setUpOpen && renderExpanded),
            })}
          >
            <div className={cs(styles.cell, styles.keywordCell)}>
              <Checkbox
                checked={!!row.selected}
                onChange={(checked) => onRowSelect?.(row.keyword, checked)}
                ariaLabel={`Select ${row.keyword}`}
              />
              <TruncatedKeywordChip label={row.keyword} />
            </div>

            <div className={styles.cell} data-label="Volume">
              <span className={styles.value}>{row.volume ?? "-"}</span>
            </div>

            <div className={styles.cell} data-label="Rank">
              <span className={styles.value}>{row.rank ?? "-"}</span>
            </div>

            <div className={styles.cell} data-label="Date range">
              <span className={cs(styles.value, styles.valueMuted)}>
                {row.dateRangeText || "-"}
              </span>
            </div>

            <div className={styles.cell} data-label="Installs">
              <span className={styles.value}>{row.installs ?? "-"}</span>
            </div>

            <div className={styles.cell} data-label="App opening">
              <AppOpeningBoxes days={row.appOpeningDays ?? 0} />
            </div>

            <div className={styles.cell} data-label="Total">
              <span className={cs(styles.value, styles.valueTotal)}>
                {row.total || "$0.00"}
              </span>
            </div>

            <div className={cs(styles.cell, styles.setUpCell)}>
              <button
                type="button"
                className={styles.setUpButton}
                onClick={() => onSetUpToggle?.(row.keyword)}
                aria-expanded={!!row.setUpOpen}
              >
                <SetUpGearIcon className={styles.setUpGear} />
                Set Up
                <SetUpChevronIcon
                  className={cs(styles.setUpChevron, {
                    [styles.setUpChevronClosed]: !row.setUpOpen,
                  })}
                />
              </button>
            </div>
          </div>

          {row.setUpOpen && renderExpanded && (
            <div className={styles.expanded}>{renderExpanded(row)}</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export type { KeywordsTableRowData };
export default NewOrderKeywordsTable;
